import Swal, { SweetAlertOptions, SweetAlertResult } from 'sweetalert2';
import 'sweetalert2/dist/sweetalert2.min.css';
import '../styles/swal-overrides.css';
import type { AppThemeId } from '../themes/themeRegistry';

type SwalTheme = 'light' | 'dark' | 'copa' | 'rosa' | 'usa';

let activeTheme: SwalTheme = 'light';

export function syncSwalTheme(themeId: AppThemeId): void {
  if (themeId === 'dark') {
    activeTheme = 'dark';
    return;
  }
  if (themeId === 'copa') {
    activeTheme = 'copa';
    return;
  }
  if (themeId === 'rosa') {
    activeTheme = 'rosa';
    return;
  }
  if (themeId === 'usa') {
    activeTheme = 'usa';
    return;
  }
  activeTheme = 'light';
}

function mergeTheme(options: SweetAlertOptions): SweetAlertOptions {
  const theme = (options.theme ?? activeTheme) as SweetAlertOptions['theme'];
  const merged: SweetAlertOptions = {
    ...options,
    theme,
  };

  if (activeTheme === 'copa') {
    return {
      ...merged,
      theme: 'copa' as SweetAlertOptions['theme'],
      confirmButtonColor: '#013f1e',
      cancelButtonColor: '#5a6f64',
    };
  }

  if (activeTheme === 'rosa') {
    return {
      ...merged,
      theme: 'rosa' as SweetAlertOptions['theme'],
      confirmButtonColor: '#D81B7A',
      cancelButtonColor: '#9e9e9e',
    };
  }

  if (activeTheme === 'usa') {
    return {
      ...merged,
      theme: 'usa' as SweetAlertOptions['theme'],
      confirmButtonColor: '#0A3161',
      cancelButtonColor: '#B22234',
    };
  }

  if (activeTheme === 'dark' && !options.confirmButtonColor) {
    return {
      ...merged,
      confirmButtonColor: '#3b82f6',
    };
  }

  return merged;
}

function patchFire(fireFn: typeof Swal.fire): typeof Swal.fire {
  const patched = ((...args: unknown[]) => {
    if (args.length === 0) {
      return fireFn(mergeTheme({}));
    }

    const first = args[0];

    if (typeof first === 'string') {
      const title = first;
      const text = typeof args[1] === 'string' ? args[1] : undefined;
      const icon = typeof args[2] === 'string' ? (args[2] as SweetAlertOptions['icon']) : undefined;

      return fireFn(mergeTheme({ title, text, icon }));
    }

    return fireFn(mergeTheme(first as SweetAlertOptions));
  }) as typeof Swal.fire;

  return patched;
}

function patchSwalInstance(instance: typeof Swal): typeof Swal {
  if ((instance.fire as { __sysupPatched?: boolean }).__sysupPatched) {
    return instance;
  }

  instance.fire = patchFire(instance.fire.bind(instance));
  (instance.fire as { __sysupPatched?: boolean }).__sysupPatched = true;

  const originalMixin = instance.mixin.bind(instance);
  instance.mixin = ((options?: SweetAlertOptions) => {
    const mixed = originalMixin(mergeTheme(options ?? {}));
    return patchSwalInstance(mixed);
  }) as typeof Swal.mixin;

  return instance;
}

patchSwalInstance(Swal);

export default Swal;
export type { SweetAlertOptions, SweetAlertResult };
